import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import { forceSignOutUser, listUserSessions, type SessionRow } from "../../../lib/admin";
import { isAdminRole, signOutToHome } from "../../../lib/auth";
import { useAuth } from "../../../lib/authContext";
import { hasVerifiedTotp, listFactors, unenrollAll } from "../../../lib/mfa";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminLoading,
  AdminPageHeader,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

const TZ = "America/New_York";

function fmtTs(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ })}`;
}

function truncateUa(ua: string | null, max = 56): string {
  if (!ua) return "—";
  return ua.length <= max ? ua : `${ua.slice(0, max)}…`;
}

export default function AdminAccountScreen() {
  const { user, role } = useAuth();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setErr(null);
    try {
      const [factors, rows] = await Promise.all([listFactors(), listUserSessions(user.id)]);
      setTotpEnabled(hasVerifiedTotp(factors));
      setSessions(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load security data");
      setTotpEnabled(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  async function onReenroll() {
    setErr(null);
    setBusy(true);
    try {
      await unenrollAll();
      router.replace("/admin/mfa/enroll" as Href);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not remove existing factors");
    } finally {
      setBusy(false);
    }
  }

  async function onSignOutAllSessions() {
    if (!user?.id) return;
    setErr(null);
    setBusy(true);
    try {
      await forceSignOutUser(user.id);
      await signOutToHome(router);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not sign out sessions");
    } finally {
      setBusy(false);
    }
  }

  if (!isAdminRole(role)) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: BLACK, padding: 32 }}>
        <AdminBanner theme={THEME} tone="error" title="No access" message="You don't have access to this page." />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: 24, maxWidth: 880 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Admin · Account"
          title="Account & security"
          subtitle={`Two-factor enrollment and active sessions for ${user?.email ?? "your account"}.`}
        />

        {err ? <View style={{ marginBottom: 14 }}><AdminError theme={THEME} message={err} /></View> : null}

        {/* Two-factor */}
        <SectionLabel>Two-factor authentication</SectionLabel>
        <View
          style={{
            marginTop: 8,
            padding: 16,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          {loading ? (
            <AdminLoading theme={THEME} />
          ) : (
            <>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: totpEnabled ? SAGE : RED }} />
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
                  {totpEnabled ? "Enabled (TOTP via authenticator app)" : "Not set up"}
                </Text>
              </View>
              <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
                {totpEnabled
                  ? "Re-enrolling will remove the existing factor and walk you through scanning a new QR code."
                  : "Walk through enrollment to scan a QR code from Microsoft Authenticator, Google Authenticator, or 1Password."}
              </Text>
              <View style={{ marginTop: 12 }}>
                <AdminButton theme={THEME} label={totpEnabled ? "Re-enroll device" : "Set up two-factor"} variant="outline" loading={busy} onPress={() => void onReenroll()} />
              </View>
            </>
          )}
        </View>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
          Recovery codes will be available in a later release.
        </Text>

        {/* Sessions */}
        <SectionLabel style={{ marginTop: 24 }}>Active sessions</SectionLabel>
        <View
          style={{
            marginTop: 8,
            padding: 16,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <Text style={{ fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
            Sessions for your account (same device may appear more than once after reconnects).
          </Text>
          <View style={{ marginTop: 10 }}>
            <AdminButton theme={THEME} label="Sign out all sessions" loading={busy} disabled={loading} onPress={() => void onSignOutAllSessions()} />
          </View>
          <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
            Signs out every session including this one — you&apos;ll need to sign in again on this device.
          </Text>
        </View>

        {loading ? null : sessions.length === 0 ? (
          <Text style={{ marginTop: 12, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            No sessions returned.
          </Text>
        ) : (
          <View
            style={{
              marginTop: 12,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: BORDER,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                paddingHorizontal: 12,
                paddingVertical: 8,
                backgroundColor: FOREST_MID,
                borderBottomWidth: 1,
                borderBottomColor: BORDER,
              }}
            >
              <HeaderCell width="26%">Started</HeaderCell>
              <HeaderCell width="26%">Last active</HeaderCell>
              <HeaderCell width="32%">Device</HeaderCell>
              <HeaderCell width="16%">IP</HeaderCell>
            </View>
            {sessions.map((s, idx) => (
              <View
                key={s.session_id}
                style={{
                  flexDirection: "row",
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  backgroundColor: idx % 2 === 0 ? "transparent" : "rgba(36,53,33,0.30)",
                  borderTopWidth: idx === 0 ? 0 : 1,
                  borderTopColor: BORDER,
                }}
              >
                <Cell width="26%">{fmtTs(s.created_at)}</Cell>
                <Cell width="26%">{fmtTs(s.updated_at)}</Cell>
                <Cell width="32%">{truncateUa(s.user_agent)}</Cell>
                <Cell width="16%">{s.ip ?? "—"}</Cell>
              </View>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

function SectionLabel({ children, style }: { children: string; style?: ViewStyle }) {
  return (
    <Text
      style={[
        {
          marginTop: 22,
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
        },
        style as object,
      ]}
    >
      {children}
    </Text>
  );
}

function HeaderCell({ width, children }: { width: string; children: string }) {
  return (
    <Text
      style={{
        width: width as unknown as number,
        fontFamily: FONTS.bodyBold,
        fontSize: 9.5,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: WM,
      }}
    >
      {children}
    </Text>
  );
}

function Cell({ width, children }: { width: string; children: string }) {
  return (
    <Text
      style={{
        width: width as unknown as number,
        fontFamily: FONTS.body,
        fontSize: 11,
        color: WS,
      }}
      numberOfLines={1}
    >
      {children}
    </Text>
  );
}

void W;
